import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { logError } from 'libs/logger';
import { COOKIES } from '../libs/constants';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_API_ROOT}`,
    prepareHeaders: (headers) => {
      const sessionId = Cookies.get(COOKIES.SessionId);
      if (sessionId) headers.set('X-EpcApi-ID', sessionId);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getListings: builder.query<Advisor[], string>({
      query: (sortId) =>
        `listings?type=cat&index=0&size=3&term=195&DealsOnly=true&SortType=${sortId}&PriceMax=9.99&availabilities=0&availabilities=3`
    }),
    searchAdvisors: builder.query<
      AutoCompleteOptionType[],
      { searchVal: string; searchCount: number }
    >({
      query: ({ searchVal, searchCount }) =>
        `search-suggestions?query=${searchVal}&domainId=1&count=${searchCount}`,
      transformResponse: (result: AdvisorSearchResult) => {
        const advisors = result.advisors.map((x) => ({
          label: x.advisorName,
          type: 'advisor',
          listingId: x.listingId
        }));
        const suggestions = result.suggestions.map((x) => ({
          label: x.suggestion,
          type: 'suggestion'
        }));
        return [...advisors, ...suggestions];
      }
    }),

    getChannelDetailsForAdvisors: builder.mutation<
      AvailabilityChannelsResult,
      number[]
    >({
      query: (advisorsIds: number[]) => ({
        url: '/availability-channels',
        method: 'POST',
        body: advisorsIds
      }),
      transformResponse: (result: any) => result.availabilityChannels
    }),

    experimentationAllocation: builder.mutation<
      ExperimentMergedProperties,
      { userId: string; keenTempUid: string }
    >({
      query: ({ userId, keenTempUid }) => ({
        url: 'experiments/buckets',
        method: 'POST',
        body: {
          userId,
          tempUid: keenTempUid,
          domainId: 1
        }
      }),
      transformResponse: ({
        mergedProperties
      }: {
        mergedProperties: ExperimentMergedProperties;
      }) => mergedProperties
    }),
    validateUserSession: builder.query<UserSession, string>({
      query: () => 'users/current',
      transformResponse: ({ user }: { user: UserSession }) => ({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        isAdvisor: user.isAdvisor,
        username: user.username
      })
    }),
    getNotifications: builder.query<Notifications, string>({
      query: (userId) =>
        `users/${userId}/notifications?_=${new Date().valueOf()}`,
      transformResponse: (resp: {
        userNotification: Notifications;
      }): Notifications => resp.userNotification
    }),
    updateNotificationsDate: builder.mutation<string, string>({
      query: (userId) => ({
        url: `users/${userId}/notifications`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: {
          Model: {
            menuLastExpandedDate: new Date().toUTCString()
          }
        }
      })
    }),
    getOffer: builder.query<OfferBanner, number>({
      query: (userId = 0) => `users/${userId}/offer-banner-info` // For local: https://sac-dev-epcws1.dev.ingenio.com:17034/api/
    })
  })
});

export const {
  useLazyGetListingsQuery,
  useLazySearchAdvisorsQuery,
  useExperimentationAllocationMutation,
  useValidateUserSessionQuery,
  useGetNotificationsQuery,
  useUpdateNotificationsDateMutation,
  useGetChannelDetailsForAdvisorsMutation,
  useLazyGetOfferQuery
} = api;

/**
 * Log a warning and show a toast!
 */
export const rtkQueryErrorLogger: Middleware =
  (_apiEndPoint: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (isRejectedWithValue(action)) {
      logError(JSON.stringify(action.payload.data));
    }
    return next(action);
  };
